/**
 * EWSAnalyticsSection - Container for risk indicator distribution charts
 *
 * Displays a grid of bar charts showing customer distribution across risk levels
 * for all 20 risk indicators. Charts are displayed 2 per row.
 */

import React, { useMemo, useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { Dropdown, DropdownTrigger, DropdownContent, DropdownItem } from 'equal-ds-ui';
import { EWSDistributionsData, IndicatorDistribution, DistributionRiskFilter, RiskLevel } from '../../../types/ews.types';
import { FILTER_POPOVER_Z } from '../../ui/FilterPopover';
import { EWSDistributionChart } from './EWSDistributionChart';

/**
 * Preferred order of indicators for display.
 * Charts will be sorted according to this order.
 */
const INDICATOR_ORDER: string[] = [
  'emi_bounces',
  'nach_bounces',
  'si_bounces',
  'late_fee_charges',
  'loan_stacking',
  'multiple_new_loans',
  'salary_drop',
  'missing_salary',
  // Remaining indicators will be shown after these
];

/**
 * Sort distributions according to preferred order.
 */
function sortDistributions(distributions: IndicatorDistribution[]): IndicatorDistribution[] {
  return [...distributions].sort((a, b) => {
    const indexA = INDICATOR_ORDER.indexOf(a.indicator);
    const indexB = INDICATOR_ORDER.indexOf(b.indicator);

    // If both are in the order list, sort by their order
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one is in the order list, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // If neither is in the list, maintain original order
    return 0;
  });
}

export interface EWSAnalyticsSectionProps {
  /** Distribution data from the API */
  distributions: EWSDistributionsData | null;

  /** Whether the data is loading */
  isLoading: boolean;

  /** Currently selected risk level filter */
  selectedRiskFilter: DistributionRiskFilter;

  /** Callback when risk filter changes */
  onRiskFilterChange: (filter: DistributionRiskFilter) => void;

  /** Callback when a bar in a chart is clicked */
  onBarClick?: (indicator: string, indicatorName: string, level: RiskLevel, levelLabel: string, count: number) => void;
}

/**
 * Risk filter options for dropdown.
 */
const RISK_FILTER_OPTIONS: { value: DistributionRiskFilter; label: string }[] = [
  { value: 'ALL', label: 'All Risk Levels' },
  { value: 'LOW', label: 'Low Risk' },
  { value: 'MEDIUM', label: 'Medium Risk' },
  { value: 'HIGH', label: 'High Risk' },
  { value: 'CRITICAL', label: 'Critical' },
];

/**
 * Loading skeleton for a single chart card.
 */
const ChartSkeleton: React.FC = () => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
    {/* Header skeleton */}
    <div className="mb-4">
      <div className="h-4 bg-gray-200 rounded w-32 mb-1.5" />
      <div className="h-3 bg-gray-200 rounded w-48" />
    </div>
    {/* Chart skeleton */}
    <div className="flex items-end justify-around h-32 pt-4">
      <div className="w-8 bg-gray-200 rounded-t" style={{ height: '80%' }} />
      <div className="w-8 bg-gray-200 rounded-t" style={{ height: '40%' }} />
      <div className="w-8 bg-gray-200 rounded-t" style={{ height: '20%' }} />
    </div>
  </div>
);

/**
 * Empty state when no data is available.
 */
const EmptyState: React.FC = () => (
  <div className="col-span-2 flex flex-col items-center justify-center py-12 text-gray-500">
    <svg
      className="w-12 h-12 mb-3 text-gray-300"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
    <p className="text-sm">No analytics data available</p>
    <p className="text-xs text-gray-400 mt-1">Data will appear once processed</p>
  </div>
);

export const EWSAnalyticsSection: React.FC<EWSAnalyticsSectionProps> = ({
  distributions,
  isLoading,
  selectedRiskFilter,
  onRiskFilterChange,
  onBarClick,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Sort distributions according to preferred order
  const sortedDistributions = useMemo(() => {
    if (!distributions?.distributions) return [];
    return sortDistributions(distributions.distributions);
  }, [distributions]);

  // Get current filter label
  const currentFilterLabel = RISK_FILTER_OPTIONS.find(
    opt => opt.value === selectedRiskFilter
  )?.label || 'All Risk Levels';

  // Handle filter selection
  const handleFilterSelect = (filter: DistributionRiskFilter) => {
    onRiskFilterChange(filter);
    setIsDropdownOpen(false);
  };

  // Render dropdown component
  const renderDropdown = () => (
    <Dropdown open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownTrigger asChild>
        <button
          className="flex items-center gap-3 px-4 py-2.5 text-base font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors min-w-[180px] justify-between"
        >
          {currentFilterLabel}
          <ChevronDown className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>
      </DropdownTrigger>
      <DropdownContent align="end" sideOffset={4} style={{ minWidth: '180px', zIndex: FILTER_POPOVER_Z }}>
        {RISK_FILTER_OPTIONS.map(option => (
          <DropdownItem
            key={option.value}
            onClick={() => handleFilterSelect(option.value)}
            className={`text-base py-2 ${selectedRiskFilter === option.value ? 'bg-gray-100' : ''}`}
          >
            {option.label}
          </DropdownItem>
        ))}
      </DropdownContent>
    </Dropdown>
  );

  // Show 6 skeleton cards while loading (3 rows of 2)
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <Sparkles className="w-5 h-5 text-blue-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Analytics</h3>
              <p className="text-sm text-gray-600 mt-0.5">
                Customer distribution by risk indicators
              </p>
            </div>
            {renderDropdown()}
          </div>
        </div>

        {/* Loading grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <ChartSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Show empty state if no distributions
  if (!distributions || sortedDistributions.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Analytics</h3>
              <p className="text-sm text-gray-600 mt-0.5">
                Customer distribution by risk indicators
              </p>
            </div>
            {renderDropdown()}
          </div>
        </div>

        {/* Empty state */}
        <div className="grid grid-cols-2">
          <EmptyState />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Analytics</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Customer distribution by risk indicators
            </p>
          </div>
          {renderDropdown()}
        </div>
      </div>

      {/* Charts grid - 3 per row on large screens, 2 on medium, 1 on small - with animation */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity duration-300 ease-in-out">
        {sortedDistributions.map(distribution => (
          <EWSDistributionChart
            key={distribution.indicator}
            distribution={distribution}
            onBarClick={onBarClick}
          />
        ))}
      </div>
    </div>
  );
};

export default EWSAnalyticsSection;
